/**
 * Hook for the per-account trusted-sender allowlist.
 *
 * A sender on this list is never quarantined, even when its domain's published policy fails.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useState } from 'react';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import {
  GetMailSenderAllowlistDocument,
  AddMailSenderAllowlistEntryDocument,
  RemoveMailSenderAllowlistEntryDocument
} from '@/graphql';

/** An address (`supplier@example.com`) or a bare domain (`example.com`). */
const ADDRESS_OR_DOMAIN =
  /^(?:[^\s@]+@)?[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;

/** Page size for the trusted-sender list. */
const PAGE_SIZE = 25;

const REFETCH_ALLOWLIST = {
  refetchQueries: [{ query: GetMailSenderAllowlistDocument, variables: { first: PAGE_SIZE } }],
  awaitRefetchQueries: true
};

export function useMailSenderAllowlist() {
  const t = useFeatureTranslation('systemSettings.mailbox');
  const { showSuccess, showError } = useToastHelpers();

  const { data, loading, error, fetchMore } = useQuery(GetMailSenderAllowlistDocument, {
    variables: { first: PAGE_SIZE },
    fetchPolicy: 'cache-and-network'
  });

  const [addEntry, { loading: adding }] = useMutation(AddMailSenderAllowlistEntryDocument);
  const [removeEntry, { loading: removing }] = useMutation(RemoveMailSenderAllowlistEntryDocument);

  const [value, setValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const connection = data?.mailSenderAllowlistConnection;
  const entries = connection?.edges.map(edge => edge.node) ?? [];
  const totalCount = connection?.totalCount ?? 0;
  const hasMore = connection?.pageInfo.hasNextPage ?? false;

  const loadMore = useCallback(async () => {
    if (!hasMore) return;
    await fetchMore({ variables: { first: PAGE_SIZE, after: connection?.pageInfo.endCursor } });
  }, [fetchMore, hasMore, connection?.pageInfo.endCursor]);

  const handleValueChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value.toLowerCase().trim());
    setValidationError(null);
  }, []);

  const handleAdd = useCallback(async () => {
    const candidate = value.toLowerCase().trim();
    if (candidate.length === 0) {
      setValidationError(t('allowlist.validation.required'));
      return;
    }
    if (!ADDRESS_OR_DOMAIN.test(candidate)) {
      setValidationError(t('allowlist.validation.invalidFormat'));
      return;
    }

    try {
      await addEntry({ variables: { input: { value: candidate } }, ...REFETCH_ALLOWLIST });
      showSuccess(t('allowlist.feedback.addSuccess'));
      setValue('');
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('already on the allowlist')) {
        setValidationError(t('allowlist.validation.duplicate'));
        return;
      }
      showError(t('allowlist.feedback.addError'));
    }
  }, [value, addEntry, showSuccess, showError, t]);

  const handleRemove = useCallback(
    async (id: string) => {
      try {
        await removeEntry({ variables: { id }, ...REFETCH_ALLOWLIST });
        showSuccess(t('allowlist.feedback.removeSuccess'));
      } catch {
        showError(t('allowlist.feedback.removeError'));
      }
    },
    [removeEntry, showSuccess, showError, t]
  );

  return {
    t,
    entries,
    totalCount,
    hasMore,
    loadMore,
    loading,
    error,
    value,
    validationError,
    handleValueChange,
    handleAdd,
    handleRemove,
    isBusy: adding || removing
  };
}
